import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Skeleton,
  Avatar, TextField, InputAdornment, TablePagination, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Casino, SportsScore, TrendingUp, TrendingDown,
  ShowChart, History, Refresh, Search,
} from '@mui/icons-material';
import { apiFetch } from '../../utils/api';

/* ─── theme ──────────────────────────────────────────────────────── */
const C = {
  bg: '#0C0D1D', card: '#0E1831', cardLight: '#121E38', border: '#1E2D55',
  primary: '#2B6EF5', text: '#F9F9F9', textMuted: '#878AA2', textSec: '#8384A5',
  success: '#0ECC68', error: '#E01B4F', warning: '#FFC23F', info: '#5581F7',
};

const fmtMoney = (n: number | string) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const fmtDate = (s: string) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const thSx = {
  bgcolor: C.bg, color: C.textSec, fontSize: '0.7rem', fontWeight: 700,
  textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  borderBottom: `1px solid ${C.border}`, py: 1.5, whiteSpace: 'nowrap' as const,
};
const tdSx = { color: C.textMuted, borderBottom: `1px solid ${C.border}33`, fontSize: '0.8rem', py: 1.4 };

/* ─── tiny helpers ──────────────────────────────────────────────── */
const SkeletonRows: React.FC<{ cols: number; rows?: number }> = ({ cols, rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: cols }).map((__, j) => (
          <TableCell key={j} sx={{ borderBottom: `1px solid ${C.border}33`, py: 1.4 }}>
            <Skeleton variant="text" sx={{ bgcolor: C.border }} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const EmptyRow: React.FC<{ cols: number; msg?: string }> = ({ cols, msg = 'No data found' }) => (
  <TableRow>
    <TableCell colSpan={cols} sx={{ py: 6, borderBottom: 'none', textAlign: 'center', color: C.textSec, fontSize: '0.85rem' }}>
      {msg}
    </TableCell>
  </TableRow>
);

const PnlChip: React.FC<{ value: number | string }> = ({ value }) => {
  const n = Number(value) || 0;
  return (
    <Chip
      label={fmtMoney(n)}
      size="small"
      sx={{
        bgcolor: n >= 0 ? 'rgba(14,204,104,0.12)' : 'rgba(224,27,79,0.12)',
        color: n >= 0 ? C.success : C.error,
        fontWeight: 700, fontSize: '0.72rem', height: 22,
      }}
    />
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TAB 1 – CASINO BETS
   ═══════════════════════════════════════════════════════════════════ */
const CasinoBetsTab: React.FC<{ userId: string; userType: 'USER' | 'STAFF' }> = ({ userId, userType }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(25);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/lords/user-detail/casino-bets?userId=${userId}&userType=${userType}&page=${page + 1}&limit=${rpp}`);
      const arr = Array.isArray(res) ? res : (res.data ?? res.bets ?? []);
      setRows(arr);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [userId, userType, page, rpp]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(r =>
    [r.game_name, r.bet_id, r.status, r.round_id, r.provider].filter(Boolean)
      .map(x => String(x).toLowerCase()).join(' ').includes(q)
  ) : rows;

  const COLS = ['#', 'Round / Bet ID', 'Game', 'Provider', 'Bet Amount', 'Win Amount', 'P&L', 'Status', 'Date'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: C.textSec, fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { bgcolor: C.bg, borderRadius: 1.5, '& fieldset': { borderColor: C.border }, '&:hover fieldset': { borderColor: C.primary }, '&.Mui-focused fieldset': { borderColor: C.primary } }, '& input': { color: C.text, fontSize: '0.83rem' } }}
        />
        <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}><Refresh fontSize="small" /></IconButton></Tooltip>
      </Box>

      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>{COLS.map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? <SkeletonRows cols={COLS.length} /> : filtered.length === 0 ? <EmptyRow cols={COLS.length} /> :
              filtered.map((r, i) => {
                const statusStr = (r.status ?? r.transaction_status ?? '').toLowerCase();
                return (
                  <TableRow key={r.bet_id ?? r.round_id ?? i} sx={{ '&:hover': { bgcolor: C.cardLight } }}>
                    <TableCell sx={tdSx}>{page * rpp + i + 1}</TableCell>
                    <TableCell sx={{ ...tdSx, fontFamily: 'monospace', color: C.info, fontSize: '0.72rem' }}>{r.bet_id ?? r.round_id ?? '—'}</TableCell>
                    <TableCell sx={{ ...tdSx, color: C.text, fontWeight: 600 }}>{r.game_name ?? r.round_id ?? '—'}</TableCell>
                    <TableCell sx={tdSx}>{r.provider ?? r.transaction_type ?? '—'}</TableCell>
                    <TableCell sx={tdSx}><Chip label={fmtMoney(r.bet_amount ?? r.amount ?? 0)} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: C.info, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                    <TableCell sx={tdSx}><Chip label={fmtMoney(r.win_amount ?? 0)} size="small" sx={{ bgcolor: 'rgba(14,204,104,0.12)', color: C.success, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                    <TableCell sx={tdSx}><PnlChip value={(Number(r.win_amount) || 0) - (Number(r.bet_amount ?? r.amount) || 0)} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Chip label={statusStr || '—'} size="small"
                        sx={{ bgcolor: statusStr === 'win' || statusStr === 'completed' ? 'rgba(14,204,104,0.15)' : statusStr === 'loss' || statusStr === 'failed' ? 'rgba(224,27,79,0.15)' : `${C.warning}22`, color: statusStr === 'win' || statusStr === 'completed' ? C.success : statusStr === 'loss' || statusStr === 'failed' ? C.error : C.warning, fontWeight: 700, fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at ?? r.date)}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination component="div" count={-1} page={page} rowsPerPage={rpp} rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
        sx={{ color: C.textSec, borderTop: `1px solid ${C.border}`, '& .MuiTablePagination-select': { color: C.text }, '& .MuiTablePagination-selectIcon': { color: C.textSec }, '& .MuiIconButton-root': { color: C.textSec, '&:hover': { color: C.text }, '&.Mui-disabled': { color: C.border } } }}
      />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TAB 2 – SPORTS BETS
   ═══════════════════════════════════════════════════════════════════ */
const SportsBetsTab: React.FC<{ userId: string; userType: 'USER' | 'STAFF' }> = ({ userId, userType }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(25);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/lords/user-detail/sports-bets?userId=${userId}&userType=${userType}&page=${page + 1}&limit=${rpp}`);
      const arr = Array.isArray(res) ? res : (res.data ?? res.list ?? []);
      setRows(arr);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [userId, userType, page, rpp]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(r =>
    [r.match_title, r.selection_name, r.game_type, r.market_type, r.bet_type, String(r.id)].filter(Boolean)
      .map(x => String(x).toLowerCase()).join(' ').includes(q)
  ) : rows;

  const COLS = ['#', 'Bet ID', 'Event', 'Market', 'Selection', 'Odds', 'Stake', 'Win/Loss', 'Status', 'Type', 'Date'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: C.textSec, fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { bgcolor: C.bg, borderRadius: 1.5, '& fieldset': { borderColor: C.border }, '&:hover fieldset': { borderColor: C.primary }, '&.Mui-focused fieldset': { borderColor: C.primary } }, '& input': { color: C.text, fontSize: '0.83rem' } }}
        />
        <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}><Refresh fontSize="small" /></IconButton></Tooltip>
      </Box>

      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>{COLS.map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? <SkeletonRows cols={COLS.length} /> : filtered.length === 0 ? <EmptyRow cols={COLS.length} /> :
              filtered.map((r, i) => (
                <TableRow key={r.id ?? i} sx={{ '&:hover': { bgcolor: C.cardLight } }}>
                  <TableCell sx={tdSx}>{page * rpp + i + 1}</TableCell>
                  <TableCell sx={{ ...tdSx, fontFamily: 'monospace', color: C.info, fontSize: '0.72rem' }}>{r.id ?? '—'}</TableCell>
                  <TableCell sx={{ ...tdSx, color: C.text, fontWeight: 600, maxWidth: 180 }}>
                    <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.text }}>{r.match_title ?? r.event_name ?? '—'}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>{r.market_type ?? r.game_type ?? '—'}</TableCell>
                  <TableCell sx={tdSx}>{r.selection_name ?? '—'}</TableCell>
                  <TableCell sx={tdSx}>{r.odds ?? '—'}</TableCell>
                  <TableCell sx={tdSx}><Chip label={fmtMoney(r.stake_amount ?? 0)} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: C.info, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                  <TableCell sx={tdSx}><PnlChip value={r.result_status === 'won' ? (Number(r.stake_amount) * (Number(r.odds) - 1)) : r.result_status === 'lost' ? -Number(r.stake_amount) : 0} /></TableCell>
                  <TableCell sx={tdSx}>
                    <Chip label={r.result_status ?? r.status ?? '—'} size="small"
                      sx={{ bgcolor: r.result_status === 'won' ? 'rgba(14,204,104,0.15)' : r.result_status === 'lost' ? 'rgba(224,27,79,0.15)' : `${C.warning}22`, color: r.result_status === 'won' ? C.success : r.result_status === 'lost' ? C.error : C.warning, fontWeight: 700, fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell sx={tdSx}><Chip label={r.bet_type ?? 'back'} size="small" sx={{ bgcolor: `${C.primary}22`, color: C.primary, fontWeight: 700, fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }} /></TableCell>
                  <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination component="div" count={-1} page={page} rowsPerPage={rpp} rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
        sx={{ color: C.textSec, borderTop: `1px solid ${C.border}`, '& .MuiTablePagination-select': { color: C.text }, '& .MuiTablePagination-selectIcon': { color: C.textSec }, '& .MuiIconButton-root': { color: C.textSec, '&:hover': { color: C.text }, '&.Mui-disabled': { color: C.border } } }}
      />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TAB 3 – SPORTS P&L REPORT
   ═══════════════════════════════════════════════════════════════════ */
const SportsPLTab: React.FC<{ userId: string; userType: 'USER' | 'STAFF' }> = ({ userId, userType }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/lords/user-detail/sports-pl?userId=${userId}&userType=${userType}`);
      const arr = Array.isArray(res) ? res : (res.data ?? res.report ?? []);
      setRows(arr);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [userId, userType]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(r =>
    [r.event_name, r.market_name, r.sport, r.date].filter(Boolean)
      .map(x => String(x).toLowerCase()).join(' ').includes(q)
  ) : rows;

  const totPL = filtered.reduce((s, r) => s + (Number(r.profit_loss ?? r.pnl ?? 0)), 0);
  const totBets = filtered.reduce((s, r) => s + (Number(r.total_bets ?? r.bets ?? 0)), 0);
  const totStake = filtered.reduce((s, r) => s + (Number(r.total_stake ?? r.stake ?? 0)), 0);

  const COLS = ['#', 'Event / Market', 'Sport', 'Date', 'Total Bets', 'Total Stake', 'Total Won', 'Net P&L'];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Records', value: filtered.length.toLocaleString(), color: C.primary },
          { label: 'Total Bets', value: totBets.toLocaleString(), color: C.info },
          { label: 'Total Stake', value: fmtMoney(totStake), color: C.warning },
          { label: 'Net P&L', value: fmtMoney(totPL), color: totPL >= 0 ? C.success : C.error },
        ].map(({ label, value, color }) => (
          <Card key={label} sx={{ bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2, px: 2.5, py: 1.5, flex: '1 1 180px', minWidth: 0 }}>
            <Typography sx={{ color: C.textSec, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
            {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: C.border }} /> :
              <Typography sx={{ color, fontSize: '1.1rem', fontWeight: 700 }}>{value}</Typography>}
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: C.textSec, fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { bgcolor: C.bg, borderRadius: 1.5, '& fieldset': { borderColor: C.border }, '&:hover fieldset': { borderColor: C.primary }, '&.Mui-focused fieldset': { borderColor: C.primary } }, '& input': { color: C.text, fontSize: '0.83rem' } }}
        />
        <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}><Refresh fontSize="small" /></IconButton></Tooltip>
      </Box>

      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>{COLS.map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? <SkeletonRows cols={COLS.length} /> : filtered.length === 0 ? <EmptyRow cols={COLS.length} /> :
              filtered.map((r, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: C.cardLight } }}>
                  <TableCell sx={tdSx}>{i + 1}</TableCell>
                  <TableCell sx={{ ...tdSx, maxWidth: 220 }}>
                    <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.text }}>{r.event_name ?? r.market_name ?? '—'}</Typography>
                    {r.market_name && r.event_name && <Typography noWrap sx={{ fontSize: '0.68rem', color: C.textSec }}>{r.market_name}</Typography>}
                  </TableCell>
                  <TableCell sx={tdSx}><Chip label={r.sport ?? r.category ?? '—'} size="small" sx={{ bgcolor: `${C.primary}22`, color: C.primary, fontSize: '0.68rem', height: 20 }} /></TableCell>
                  <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>{fmtDate(r.date ?? r.settled_at ?? r.created_at)}</TableCell>
                  <TableCell sx={tdSx}>{(r.total_bets ?? r.bets ?? '—').toLocaleString?.() ?? r.total_bets ?? '—'}</TableCell>
                  <TableCell sx={tdSx}><Chip label={fmtMoney(r.total_stake ?? r.stake ?? 0)} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: C.info, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                  <TableCell sx={tdSx}><Chip label={fmtMoney(r.total_won ?? r.won ?? 0)} size="small" sx={{ bgcolor: 'rgba(14,204,104,0.12)', color: C.success, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                  <TableCell sx={tdSx}><PnlChip value={r.profit_loss ?? r.pnl ?? 0} /></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TAB 4 – CASINO P&L REPORT
   ═══════════════════════════════════════════════════════════════════ */
const CasinoPLTab: React.FC<{ userId: string; userType: 'USER' | 'STAFF' }> = ({ userId, userType }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/lords/user-detail/casino-pl?userId=${userId}&userType=${userType}`);
      const arr = Array.isArray(res) ? res : (res.data ?? res.report ?? []);
      setRows(arr);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [userId, userType]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(r =>
    [r.game_name, r.provider, r.game_type, r.date].filter(Boolean)
      .map(x => String(x).toLowerCase()).join(' ').includes(q)
  ) : rows;

  const totPL = filtered.reduce((s, r) => s + (Number(r.profit_loss ?? r.pnl ?? 0)), 0);
  const totWager = filtered.reduce((s, r) => s + (Number(r.total_wager ?? r.total_bet ?? 0)), 0);
  const totRounds = filtered.reduce((s, r) => s + (Number(r.total_rounds ?? r.rounds ?? r.bets ?? 0)), 0);

  const COLS = ['#', 'Game / Provider', 'Type', 'Date', 'Rounds', 'Total Wager', 'Total Won', 'Net P&L'];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Games', value: filtered.length.toLocaleString(), color: C.primary },
          { label: 'Total Rounds', value: totRounds.toLocaleString(), color: C.info },
          { label: 'Total Wager', value: fmtMoney(totWager), color: C.warning },
          { label: 'Net P&L', value: fmtMoney(totPL), color: totPL >= 0 ? C.success : C.error },
        ].map(({ label, value, color }) => (
          <Card key={label} sx={{ bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2, px: 2.5, py: 1.5, flex: '1 1 180px', minWidth: 0 }}>
            <Typography sx={{ color: C.textSec, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
            {loading ? <Skeleton variant="text" width={80} sx={{ bgcolor: C.border }} /> :
              <Typography sx={{ color, fontSize: '1.1rem', fontWeight: 700 }}>{value}</Typography>}
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: C.textSec, fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { bgcolor: C.bg, borderRadius: 1.5, '& fieldset': { borderColor: C.border }, '&:hover fieldset': { borderColor: C.primary }, '&.Mui-focused fieldset': { borderColor: C.primary } }, '& input': { color: C.text, fontSize: '0.83rem' } }}
        />
        <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}><Refresh fontSize="small" /></IconButton></Tooltip>
      </Box>

      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>{COLS.map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? <SkeletonRows cols={COLS.length} /> : filtered.length === 0 ? <EmptyRow cols={COLS.length} /> :
              filtered.map((r, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: C.cardLight } }}>
                  <TableCell sx={tdSx}>{i + 1}</TableCell>
                  <TableCell sx={{ ...tdSx, maxWidth: 200 }}>
                    <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.text }}>{r.game_name ?? '—'}</Typography>
                    <Typography noWrap sx={{ fontSize: '0.68rem', color: C.textSec }}>{r.provider ?? ''}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}><Chip label={r.game_type ?? r.category ?? '—'} size="small" sx={{ bgcolor: `${C.info}22`, color: C.info, fontSize: '0.68rem', height: 20 }} /></TableCell>
                  <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>{fmtDate(r.date ?? r.created_at)}</TableCell>
                  <TableCell sx={tdSx}>{(r.total_rounds ?? r.rounds ?? r.bets ?? '—').toLocaleString?.() ?? '—'}</TableCell>
                  <TableCell sx={tdSx}><Chip label={fmtMoney(r.total_wager ?? r.total_bet ?? 0)} size="small" sx={{ bgcolor: 'rgba(43,110,245,0.12)', color: C.info, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                  <TableCell sx={tdSx}><Chip label={fmtMoney(r.total_won ?? r.won ?? 0)} size="small" sx={{ bgcolor: 'rgba(14,204,104,0.12)', color: C.success, fontWeight: 600, fontSize: '0.72rem', height: 22 }} /></TableCell>
                  <TableCell sx={tdSx}><PnlChip value={r.profit_loss ?? r.pnl ?? 0} /></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TAB 5 – NET EXPOSURE
   ═══════════════════════════════════════════════════════════════════ */
const NetExposureTab: React.FC<{ userId: string; userType: 'USER' | 'STAFF' }> = ({ userId, userType }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [totalExposure, setTotalExposure] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/lords/net-exposure/sports?userId=${userId}&userType=${userType}`);
      setEvents(data.data ?? []);
      setTotalExposure(data.meta?.totalExposure ?? 0);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  }, [userId, userType]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>Net Exposure</Typography>
          <Typography sx={{ color: C.textSec, fontSize: '0.78rem' }}>
            Total: <span style={{ color: totalExposure > 0 ? C.error : C.success, fontWeight: 700 }}>{fmtMoney(totalExposure)}</span>
          </Typography>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}><Refresh fontSize="small" /></IconButton></Tooltip>
      </Box>

      {loading ? (
        <TableContainer sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
          <Table size="small">
            <TableHead><TableRow>{['Event', 'Market Type', 'Team/Selection', 'Exposure'].map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody><SkeletonRows cols={4} /></TableBody>
          </Table>
        </TableContainer>
      ) : events.length === 0 ? (
        <Card sx={{ bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2, py: 6, textAlign: 'center' }}>
          <ShowChart sx={{ fontSize: 40, color: C.border, mb: 1 }} />
          <Typography sx={{ color: C.textSec, fontSize: '0.85rem' }}>No active exposure</Typography>
        </Card>
      ) : (
        events.map((ev: any) => (
          <Card key={ev.eventId} sx={{ bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2, mb: 2 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Chip label={ev.category ?? 'Sports'} size="small" sx={{ bgcolor: `${C.primary}22`, color: C.primary, fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
              <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.88rem' }}>{ev.eventName}</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>{['Market Type', 'Team / Selection', 'Exposure', 'User'].map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {(ev.markets ?? []).map((m: any, j: number) => (
                    <TableRow key={j} sx={{ '&:hover': { bgcolor: C.cardLight } }}>
                      <TableCell sx={tdSx}>{m.gameType ?? '—'}</TableCell>
                      <TableCell sx={{ ...tdSx, color: C.text, fontWeight: 600 }}>{m.teamName ?? '—'}</TableCell>
                      <TableCell sx={tdSx}>
                        <Chip label={fmtMoney(m.exposure ?? 0)} size="small"
                          sx={{ bgcolor: (m.exposure ?? 0) > 0 ? 'rgba(224,27,79,0.15)' : 'rgba(14,204,104,0.12)', color: (m.exposure ?? 0) > 0 ? C.error : C.success, fontWeight: 700, fontSize: '0.72rem', height: 22 }} />
                      </TableCell>
                      <TableCell sx={tdSx}>{m.userName ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ))
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   TAB 6 – TRANSACTION HISTORY (OVERALL)
   ═══════════════════════════════════════════════════════════════════ */
const TransactionHistoryTab: React.FC<{ userId: string; userType: 'USER' | 'STAFF' }> = ({ userId, userType }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(25);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/lords/user-detail/transactions?userId=${userId}&userType=${userType}&page=${page + 1}&limit=${rpp}`);
      const arr = Array.isArray(res) ? res : (res.data ?? res.rows ?? res.transactions ?? []);
      setRows(arr);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [userId, userType, page, rpp]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(r =>
    [r.from_name, r.to_name, r.type, r.direction, String(r.id)].filter(Boolean)
      .map(x => String(x).toLowerCase()).join(' ').includes(q)
  ) : rows;

  const COLS = ['#', 'Txn ID', 'Type', 'From', 'To', 'Amount', 'Direction', 'Date'];

  const directionColor = (d: string) => {
    if (!d) return C.textSec;
    const dl = d.toLowerCase();
    if (dl === 'deposit' || dl === 'credit') return C.success;
    if (dl === 'withdraw' || dl === 'debit') return C.error;
    return C.warning;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: C.textSec, fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { bgcolor: C.bg, borderRadius: 1.5, '& fieldset': { borderColor: C.border }, '&:hover fieldset': { borderColor: C.primary }, '&.Mui-focused fieldset': { borderColor: C.primary } }, '& input': { color: C.text, fontSize: '0.83rem' } }}
        />
        <Tooltip title="Refresh"><IconButton onClick={load} size="small" sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}><Refresh fontSize="small" /></IconButton></Tooltip>
      </Box>

      <TableContainer sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
        <Table size="small">
          <TableHead>
            <TableRow>{COLS.map((h, i) => <TableCell key={i} sx={thSx}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? <SkeletonRows cols={COLS.length} /> : filtered.length === 0 ? <EmptyRow cols={COLS.length} /> :
              filtered.map((r, i) => (
                <TableRow key={r.id ?? i} sx={{ '&:hover': { bgcolor: C.cardLight } }}>
                  <TableCell sx={tdSx}>{page * rpp + i + 1}</TableCell>
                  <TableCell sx={{ ...tdSx, fontFamily: 'monospace', color: C.info, fontSize: '0.72rem' }}>{r.id ?? '—'}</TableCell>
                  <TableCell sx={tdSx}>
                    <Chip label={r.type ?? r.txn_type ?? 'Transfer'} size="small"
                      sx={{ bgcolor: `${C.warning}22`, color: C.warning, fontWeight: 700, fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.78rem', color: C.text, fontWeight: 600 }}>{r.from_name ?? '—'}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: C.textSec }}>{r.from_type ?? ''}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: '0.78rem', color: C.text, fontWeight: 600 }}>{r.to_name ?? '—'}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: C.textSec }}>{r.to_type ?? ''}</Typography>
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Chip label={fmtMoney(r.amount ?? 0)} size="small"
                      sx={{ bgcolor: `${directionColor(r.direction ?? r.type)}22`, color: directionColor(r.direction ?? r.type), fontWeight: 700, fontSize: '0.72rem', height: 22 }} />
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Chip label={r.direction ?? r.type ?? '—'} size="small"
                      sx={{ bgcolor: `${directionColor(r.direction ?? r.type)}22`, color: directionColor(r.direction ?? r.type), fontWeight: 700, fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination component="div" count={-1} page={page} rowsPerPage={rpp} rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
        sx={{ color: C.textSec, borderTop: `1px solid ${C.border}`, '& .MuiTablePagination-select': { color: C.text }, '& .MuiTablePagination-selectIcon': { color: C.textSec }, '& .MuiIconButton-root': { color: C.textSec, '&:hover': { color: C.text }, '&.Mui-disabled': { color: C.border } } }}
      />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════ */
type TabId = 'casino-bets' | 'sports-bets' | 'sports-pl' | 'casino-pl' | 'exposure' | 'transactions';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'casino-bets',  label: 'Casino Bets',    icon: <Casino sx={{ fontSize: 16 }} /> },
  { id: 'sports-bets',  label: 'Sports Bets',    icon: <SportsScore sx={{ fontSize: 16 }} /> },
  { id: 'sports-pl',   label: 'Sports P&L',      icon: <TrendingUp sx={{ fontSize: 16 }} /> },
  { id: 'casino-pl',   label: 'Casino P&L',      icon: <TrendingDown sx={{ fontSize: 16 }} /> },
  { id: 'exposure',    label: 'Net Exposure',     icon: <ShowChart sx={{ fontSize: 16 }} /> },
  { id: 'transactions',label: 'Transactions',     icon: <History sx={{ fontSize: 16 }} /> },
];

const avatarColor = (name: string) => {
  const colors = [C.primary, '#7B5EF5', C.success, C.warning, C.error, C.info];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const UserDetailPage: React.FC = () => {
  const { userType, userId } = useParams<{ userType: string; userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabId>('casino-bets');

  // name / email / role passed via navigation state
  const state = (location.state ?? {}) as { name?: string; email?: string; role?: string };
  const displayName = state.name ?? userId ?? '—';
  const displayEmail = state.email ?? '';
  const displayRole = state.role ?? '';

  const ut = (userType === 'staff' ? 'STAFF' : 'USER') as 'USER' | 'STAFF';
  const uid = userId ?? '';

  const initials = displayName && displayName !== '—'
    ? displayName.slice(0, 2).toUpperCase()
    : '??';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, minHeight: '100vh', bgcolor: C.bg }}>
      {/* ── Header bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Tooltip title="Back to Users">
          <IconButton onClick={() => navigate('/users')}
            sx={{ color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 1.5, '&:hover': { color: C.primary, borderColor: C.primary } }}>
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>

        <Avatar sx={{ width: 42, height: 42, fontSize: '0.82rem', fontWeight: 700, bgcolor: avatarColor(displayName), flexShrink: 0 }}>
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            <Chip
              label={ut === 'STAFF' ? (displayRole || 'Staff') : 'User'}
              size="small"
              sx={{
                bgcolor: ut === 'STAFF' ? 'rgba(255,194,63,0.18)' : 'rgba(43,110,245,0.18)',
                color: ut === 'STAFF' ? C.warning : C.primary,
                fontWeight: 700, fontSize: '0.68rem', height: 20,
              }}
            />
            {ut === 'STAFF' && (
              <Chip label="Downline View" size="small"
                sx={{ bgcolor: 'rgba(123,94,245,0.18)', color: '#7B5EF5', fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.3, flexWrap: 'wrap' }}>
            {displayEmail && <Typography sx={{ color: C.textSec, fontSize: '0.75rem' }}>{displayEmail}</Typography>}
            <Typography sx={{ color: C.textSec, fontSize: '0.75rem', fontFamily: 'monospace' }}>ID: {uid}</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Card sx={{ bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 2 }}>
        <Box sx={{ borderBottom: `1px solid ${C.border}`, overflowX: 'auto' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                color: C.textSec, textTransform: 'none', fontWeight: 600,
                fontSize: '0.83rem', minHeight: 44, gap: 0.6, px: 2,
                '&.Mui-selected': { color: C.primary },
              },
              '& .MuiTabs-indicator': { bgcolor: C.primary, height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            {TABS.map(t => (
              <Tab key={t.id} value={t.id} label={t.label} icon={t.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {activeTab === 'casino-bets'   && <CasinoBetsTab    userId={uid} userType={ut} />}
          {activeTab === 'sports-bets'   && <SportsBetsTab    userId={uid} userType={ut} />}
          {activeTab === 'sports-pl'     && <SportsPLTab      userId={uid} userType={ut} />}
          {activeTab === 'casino-pl'     && <CasinoPLTab      userId={uid} userType={ut} />}
          {activeTab === 'exposure'      && <NetExposureTab   userId={uid} userType={ut} />}
          {activeTab === 'transactions'  && <TransactionHistoryTab userId={uid} userType={ut} />}
        </Box>
      </Card>
    </Box>
  );
};

export default UserDetailPage;
